import React, { useState, useMemo, useEffect } from "react";

import { getErrorMessage } from "utils";
import { listedCompanies } from "utils/listedCompany";
import { companyLabel } from "utils/companyLabel";

import { useSnackbar } from "contexts";
import { useUpdatePackage, useCompaniesQuery } from "hooks/api";

import { Stack, Typography } from "@mui/material";

import { Modal } from "@repo/ui";
import { Button, Alert } from "@repo/ui";
import { CompanyAvatar, Select, type SelectItem } from "components";
import { api } from "services";

/**
 * "Assign to Company" — the one modal behind BOTH assign entry points, because they are the same
 * decision made about different units of the same stock:
 *
 *  - `packageObj`  → assigns a whole PACKAGE (`PATCH /packages/:id`). Every tag in the box counts
 *                    as that company's, since the scan-ownership guard falls back to the
 *                    package's company when a tag carries none of its own.
 *  - `tagUids`     → assigns loose TAGS (`POST /packages/tags/assign-company`), for stock already
 *                    encoded and sitting in the unassigned pool.
 *
 * Kept as one component rather than two near-identical modals so the warning copy — the part that
 * actually explains the consequence — can never drift between them.
 */
interface SetPackageCompanyModalProps {
  packageObj?: any;
  /** Tag-mode: the uids to assign. Mutually exclusive with `packageObj`. */
  tagUids?: string[];
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

export const SetPackageCompanyModal = (props: SetPackageCompanyModalProps) => {
  const { packageObj, tagUids, open, onClose = () => {}, refetch } = props;

  const isTagMode = Array.isArray(tagUids) && tagUids.length > 0;

  const { open: notification } = useSnackbar();
  const updateMutation = useUpdatePackage();
  const [assigningTags, setAssigningTags] = useState(false);
  // VRG-808 — this is the SAME "Assign to Company" dialog the Tags kebab and the Packages
  // kebab both open, and it is a tag-assignment surface exactly like EncodeTagsModal, so it
  // takes the same deliberate opt-in. Fixing EncodeTagsModal alone left this one excluding
  // demos, which is how a demo was selectable from "Add tags" and not from the kebab.
  const { data: companiesData } = useCompaniesQuery(
    { limit: 200, includeDemo: true },
    { enabled: open },
  );

  const companies = useMemo(
    () => listedCompanies(companiesData),
    [companiesData],
  );

  const options: SelectItem[] = useMemo(
    () =>
      companies.map((company) => ({
        label: company.name,
        value: company.id,
        // The SAME avatar every other company surface uses (CompanyAvatar: rounded-square
        // with a logo plate, initials fallback) — not the Select's generic circular one,
        // which reads as a person and mismatched every company preview in the app.
        adornment: (
          <CompanyAvatar logo={company.logo} name={company.name} size={32} />
        ),
        // VRG-808 — demo companies are admitted here, badged. Tag/package assignment to a
        // demo is the point: a prospect gets a physical product whose tag opens their own
        // branded certificate, and takeover is in-place, so the tag keeps working when they
        // sign. The badge is the safeguard the repository's blanket exclusion used to be.
        rightLabel: companyLabel(company.kind),
      })),
    [companies],
  );

  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    packageObj?.company_id || null,
  );

  // VRG-345 — tags among `tagUids` that already belong to a DIFFERENT company, so Save would MOVE
  // them. This modal sends `reassign: true` unconditionally, which is the right default (the
  // operator picked a company for tags they can see) but was silently taking tags off another
  // brand with nothing on screen to say so. EncodeTagsModal already warns before its equivalent
  // commit via the same endpoint; this is that warning, so the two assign surfaces agree.
  const [conflicts, setConflicts] = useState<{ uid: string; name: string }[]>(
    [],
  );
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedCompanyId(packageObj?.company_id || null);
    }
  }, [open, packageObj]);

  useEffect(() => {
    if (!open) {
      setConflicts([]);
      return;
    }
    if (!isTagMode || !selectedCompanyId) {
      setConflicts([]);
      return;
    }
    let cancelled = false;
    setCheckingConflicts(true);
    // A failed check clears the warning rather than blocking Save: the assignment itself is
    // reversible (reassign again), so refusing to let the operator act because an advisory
    // lookup failed would be the worse trade. Matches EncodeTagsModal's `catch { return [] }`.
    api
      .post("/packages/tags/assignment-conflicts", {
        uids: tagUids,
        kind: "company",
        targetId: selectedCompanyId,
      })
      .then((resp) => {
        if (!cancelled) setConflicts(resp?.data?.conflicts ?? []);
      })
      .catch(() => {
        if (!cancelled) setConflicts([]);
      })
      .finally(() => {
        if (!cancelled) setCheckingConflicts(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isTagMode, selectedCompanyId, tagUids]);

  const selectedCompanyName = useMemo(
    () => options.find((o) => o.value === selectedCompanyId)?.label ?? null,
    [options, selectedCompanyId],
  );

  const busy = updateMutation.isPending || assigningTags;

  const handleAssignTags = async () => {
    if (!selectedCompanyId) return;
    setAssigningTags(true);
    try {
      // `reassign: true` — the operator picked a company for tags they can see are already
      // somewhere; refusing silently would look like the action did nothing.
      const resp = await api.post("/packages/tags/assign-company", {
        companyId: selectedCompanyId,
        uids: tagUids,
        reassign: true,
      });
      // `api` resolves on 4xx rather than throwing (see services/index.ts errorHandler), so the
      // status has to be checked explicitly or a 403 reads as a successful assignment.
      const status = resp?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(resp?.data?.message || "Could not assign the tags");
      }
      // NO SUCCESS TOAST ON THE EXPECTED PATH — deliberate, per the repo's toast policy
      // ("success of a visible action gets no toast; an invisible result does"). The result IS
      // visible: the Tags list carries a default-visible Company column, `refetch()` fires here,
      // and the bulk selection survives the refetch, so the rows the operator just acted on are
      // the highlighted ones showing the new company. A toast would restate what is on screen.
      //
      // The ONE outcome the table cannot express is a SHORTFALL — `assignedCount` below what was
      // requested means some uids matched no row (deleted between listing and Save). Nothing on
      // screen says which, or that it happened at all, so that case does get a notification.
      const requested = tagUids!.length;
      const count: number = resp?.data?.assignedCount ?? requested;
      handleClose();
      refetch();
      if (count < requested) {
        notification({
          type: "warning",
          title: `Assigned ${count} of ${requested} tags. ${
            requested - count === 1 ? "One tag" : `${requested - count} tags`
          } could not be found.`,
        });
      }
    } catch (error) {
      notification({
        type: "error",
        title: getErrorMessage(error) || "Could not assign the tags",
      });
    } finally {
      setAssigningTags(false);
    }
  };

  const handleSetCompany = () => {
    if (isTagMode) {
      handleAssignTags();
      return;
    }
    if (!packageObj) return;

    updateMutation.mutate(
      {
        id: String(packageObj.id),
        company_id: selectedCompanyId,
      },
      {
        onSuccess: () => {
          handleClose();
          refetch();
        },
        onError: (error: any) => {
          const message = getErrorMessage(error);
          notification({
            type: "error",
            title: message,
          });
        },
      },
    );
  };

  const handleClose = () => {
    setSelectedCompanyId(null);
    onClose();
  };

  // What the assignment is ABOUT, named the way the operator sees it elsewhere. For a package
  // that is its NAME (the minted `<SKU>-<date>-<hex>` shown in the Packages list), never
  // `PKG-<uuid>`: the uuid appears in no other surface, so quoting it here asked the operator to
  // match an identifier against a list that shows a different one.
  const subject = isTagMode
    ? `${tagUids!.length} tag${tagUids!.length === 1 ? "" : "s"}`
    : (packageObj?.name ?? "this package");

  return (
    <Modal
      title="Assign to Company"
      open={open}
      onClose={handleClose}
      actions={
        <>
          <Button
            disableElevation
            variant="secondary"
            onClick={() => handleClose()}
            disabled={busy}
          >
            Cancel
          </Button>

          <Button
            disableElevation
            onClick={() => handleSetCompany()}
            // Held while the conflict lookup is in flight, so a fast Save cannot commit a MOVE
            // before the warning that describes it has rendered. A warning the operator can
            // outrun is not a warning.
            disabled={
              busy || checkingConflicts || (isTagMode && !selectedCompanyId)
            }
            loading={busy}
          >
            Save
          </Button>
        </>
      }
    >
      <Stack spacing="16px">
        <Typography
          variant="body3"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {isTagMode
            ? `Hand ${subject} to a company. They become that company's stock.`
            : `Hand ${subject} to a company. Every tag in it counts as that company's stock.`}
        </Typography>
        <Alert
          severity="warning"
          description={`This changes who owns ${isTagMode ? "these tags" : "the package"}. Only ${
            selectedCompanyName ?? "the assigned company"
          } will be able to create certificates with these tags. Reversible on reassignment.`}
          sx={{ padding: "12px 24px" }}
        />
        {conflicts.length > 0 && (
          <Alert
            severity="warning"
            description={`${conflicts.length} of these tag${
              conflicts.length === 1 ? " is" : "s are"
            } already assigned to another company. Saving will move ${
              conflicts.length === 1 ? "it" : "them"
            } here.`}
            sx={{ padding: "12px 24px" }}
          />
        )}
        <Select
          label="Company"
          labelVariant="body2"
          options={options}
          value={selectedCompanyId || ""}
          placeholder="Select a company"
          onChange={(value) => {
            const companyId = typeof value === "string" ? value || null : null;
            setSelectedCompanyId(companyId);
          }}
          helperText={
            isTagMode
              ? "Select the company to assign these tags to"
              : "Select the company to assign this package to"
          }
        />
      </Stack>
    </Modal>
  );
};
